import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import { Icon, type IconName } from './icons';

type Tone = 'info' | 'success' | 'danger';

interface Toast {
  id: number;
  tone: Tone;
  message: ReactNode;
  /** Milliseconds on screen. A failure stays until dismissed. */
  duration: number;
}

interface ToastApi {
  show: (message: ReactNode, tone?: Tone, duration?: number) => void;
  success: (message: ReactNode) => void;
  error: (message: ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const tones: Record<Tone, { icon: IconName; accent: string }> = {
  info: { icon: 'info', accent: 'text-[var(--ff-text-muted)]' },
  success: { icon: 'check', accent: 'text-[var(--ff-success)]' },
  danger: { icon: 'alert', accent: 'text-[var(--ff-danger)]' },
};

/**
 * Transient messages, stacked in a corner.
 *
 * For the things that used to be an inline banner somewhere on the page: a rename that
 * failed, a duplicate that worked. A banner has to be looked at to be noticed and has to
 * be placed somewhere by whoever writes the screen; a toast arrives in the same place
 * every time and takes itself away.
 *
 * Failures do not auto-dismiss. A success can be missed harmlessly — the change is on
 * screen — but a message explaining why something did not happen has to survive long
 * enough to be read.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: ReactNode, tone: Tone = 'info', duration?: number) => {
    const id = nextId.current++;
    setToasts((current) => [
      // Three at once is already a lot to read; older ones give way.
      ...current.slice(-2),
      { id, tone, message, duration: duration ?? (tone === 'danger' ? 0 : 4000) },
    ]);
  }, []);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message) => show(message, 'success'),
    error: (message) => show(message, 'danger'),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/*
        Polite, not assertive: these announce something that already happened, and an
        assertive region interrupts whatever a screen reader was in the middle of saying.
      */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))]
                   flex-col gap-2"
      >
        {toasts.map((toast) => (
          <ToastRow key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside a ToastProvider.');
  return api;
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const tone = tones[toast.tone];

  useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = setTimeout(onDismiss, toast.duration);
    return () => clearTimeout(timer);
  }, [toast.duration, onDismiss]);

  return (
    <div
      className="pointer-events-auto flex items-start gap-2.5 rounded-lg border
                 border-[var(--ff-border)] bg-[var(--ff-surface)] p-3
                 shadow-[var(--ff-shadow-lg)]"
    >
      <Icon name={tone.icon} size={16} className={clsx('mt-0.5 shrink-0', tone.accent)} />
      <div className="min-w-0 flex-1 text-[13.5px] leading-relaxed">{toast.message}</div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="-m-1 shrink-0 rounded p-1 text-[var(--ff-text-faint)]
                   hover:text-[var(--ff-text)]"
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
